import React, { useEffect, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import PopoverRebootCustom from "../../../components/PopoverRebootCustom";
import { ExpandMore as ExpandMoreIcon } from "@mui/icons-material";
import { API_BASE_URL } from "../../../../config";
import { useDispatch } from "react-redux";
import { showSnackbar } from "../../../../redux/slices/snackbar";
import { useNavigate, useParams } from "react-router-dom";
import { useFormRCAutoDetails } from "../rcautoContext";

const getStatusChip = (status, label) => {
  switch (status) {
    case 1:
      return (
        <Chip
          label={label}
          sx={{
            bgcolor: "success.main",
            color: "white",
            fontWeight: "bold",
            borderRadius: "6px",
          }}
          size="small"
        />
      );
    case 4:
      return (
        <Chip
          label={label}
          sx={{
            bgcolor: "error.main",
            color: "white",
            fontWeight: "bold",
            borderRadius: "6px",
          }}
          size="small"
        />
      );
    case 2:
      return (
        <Chip
          label={label}
          sx={{
            bgcolor: "orange",
            color: "white",
            fontWeight: "bold",
            borderRadius: "6px",
          }}
          size="small"
        />
      );
    case 5:
      return (
        <Chip
          label={label}
          sx={{
            bgcolor: "warning.main",
            color: "white",
            fontWeight: "bold",
            borderRadius: "6px",
          }}
          size="small"
        />
      );
    // Add more cases here later
    default:
      return <Chip label={label} size="small" />;
  }
};

const Controlli = () => {
  const { idTarga } = useParams();
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState();
  const navigate = useNavigate();

  const dispatchSnackbar = useDispatch();

  // Snackbar helper
  const handleSnackbar = (message, severity) => {
    dispatchSnackbar(showSnackbar({ message, severity }));
  };

  async function getControlli() {
    const token = sessionStorage.getItem("token");
    setLoading(true);
    try {
      const requestOptions = {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          authorization: "Bearer " + token,
        },
      };

      const response = await fetch(
        `${API_BASE_URL}/api/rcAuto/controlli/${idTarga}`,
        requestOptions,
      );

      const responseData = await response.json();
      if (responseData.status === -1) {
        handleSnackbar(responseData.message, "error");
        navigate("/auth/sign-in");
      }
      if (responseData.status === 0) {
        handleSnackbar(responseData.message, "error");
      }
      if (responseData.status === 1) {
        setData(responseData.data.Controlli);
      }
      setLoading(false);
    } catch (error) {
      handleSnackbar("Errore durante il salvataggio.", "error");
    } finally {
      setLoading(false);
    }
  }

  async function servicesNewUpdate() {
    const token = sessionStorage.getItem("token");
    try {
      const requestOptions = {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          authorization: "Bearer " + token,
        },
        body: JSON.stringify({
          action: "reboot",
          data: { idUID: selectedItem?.idUID, scope: 0 },
        }),
      };

      const response = await fetch(
        `${API_BASE_URL}/api/services/${selectedItem?.idUID}/reboot`,
        requestOptions,
      );

      const responseData = await response.json();
      if (responseData.status === -1) {
        handleSnackbar(responseData.message, "error");
        navigate("/auth/sign-in");
      }
      if (responseData.status === 0) {
        handleSnackbar(responseData.message, "error");
      }
      if (responseData.status === 1) {
        handleSnackbar(responseData.message, "success");
        return true;
      }
      return false;
    } catch (error) {
      handleSnackbar("Errore durante il salvataggio.", "error");
      return false;
    }
  }

  useEffect(() => {
    getControlli();
  }, []);

  const handleOpenPopover = (event, item) => {
    setAnchorEl(event.currentTarget);
    setSelectedItem(item);
  };

  const handleClosePopover = () => {
    setAnchorEl(null);
    setSelectedItem(null);
  };

  // Replace this with your actual reboot API call
  const handleRebootConfirm = async () => {
    await servicesNewUpdate();
    await getControlli();
    handleClosePopover();
  };

  if (data?.controlliKO?.length === 0 && data?.controllOK?.length === 0) {
    return (
      <Box>
        <Typography>Nessun controllo trovato</Typography>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ width: "100%", overflow: "auto" }}>
      <Box
        sx={{
          width: "100%",
          maxHeight: 500,
          overflow: "auto",
        }}
      >
        <Table stickyHeader sx={{ minWidth: 1000, tableLayout: "fixed" }}>
          <TableHead>
            <TableRow>
              <TableCell>Nome</TableCell>
              <TableCell>Fonte</TableCell>
              <TableCell>Inizio / Fine Ricerca</TableCell>
              <TableCell>Stato / Note</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data?.controlliOK.map((item, i) => (
              <TableRow
                key={i}
                sx={{
                  height: "40px",
                  "& .MuiTableCell-root": {
                    padding: "2px 16px",
                    height: "40px",
                  },
                }}
              >
                <TableCell>
                  <Box display="flex" gap={1} alignItems="center">
                    <Tooltip title="Riavvia">
                      <IconButton
                        sx={{ color: "green" }}
                        onClick={(e) => handleOpenPopover(e, item)}
                      >
                        <RestartAltIcon />
                      </IconButton>
                    </Tooltip>
                    <Box display="flex" flexDirection="column">
                      <Typography fontWeight="bold" variant="body2">
                        {item.Nome}
                      </Typography>
                      <Typography variant="caption">
                        {item.Provenienza}
                      </Typography>
                    </Box>
                  </Box>
                </TableCell>
                <TableCell>
                  <Box display="flex" flexDirection="column">
                    <Typography fontWeight="bold">
                      {item.ProvenienzaNome}
                    </Typography>
                    <Typography variant="body2">
                      Fascia: {item.IdFascia}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Box display="flex" flexDirection="column">
                    <Typography fontWeight="bold">
                      {item.Data_Inizio_Ricerca}
                    </Typography>
                    <Typography>{item.Data_Fine_Ricerca}</Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Box
                    display="flex"
                    flexDirection="column"
                    alignItems="flex-start"
                    gap={1}
                  >
                    {getStatusChip(item.Stato, item.StatoDesc)}
                    <Typography variant="body2">{item.Note}</Typography>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        <PopoverRebootCustom
          isRebootPopoverOpen={Boolean(anchorEl)}
          handleCloseRebootPopup={handleClosePopover}
          rebootRef={anchorEl}
          onConfirm={handleRebootConfirm}
        />
      </Box>
      <Box
        mt={10}
        sx={{
          width: "100%",
          maxHeight: 500,
          overflow: "auto",
        }}
      >
        {data?.controlliKO.map((item, i) => (
          <Accordion key={i}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box>
                <Typography fontWeight="bold" variant="body2">
                  {item.Nome}
                </Typography>
                <Box display="flex" gap={4}>
                  <Typography variant="caption">{item.Provenienza}</Typography>|
                  <Typography variant="caption">
                    Totale Fasce: {item.TotaleFasce}
                  </Typography>
                </Box>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  overflow: "auto",
                }}
              >
                <Table sx={{ minWidth: 1000, tableLayout: "fixed" }}>
                  <TableHead>
                    <TableRow>
                      <TableCell>Fascia</TableCell>
                      <TableCell>Filtro</TableCell>
                      <TableCell>Condizione</TableCell>
                      <TableCell>Valore Rilevato</TableCell>
                      <TableCell>Note</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {item.errori.map((errorItem, i) => (
                      <TableRow
                        key={i}
                        sx={{
                          height: "40px",
                          "& .MuiTableCell-root": {
                            padding: "2px 16px",
                            height: "40px",
                          },
                        }}
                      >
                        <TableCell>
                          <Typography>{errorItem.fascia}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography>{errorItem.filtro}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography>{errorItem.condizione}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography>{errorItem.valoreRilevato}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography>{errorItem.note}</Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            </AccordionDetails>
          </Accordion>
        ))}
      </Box>
    </Box>
  );
};

export default Controlli;
